// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React from 'react';
import * as yup from 'yup';
import {
  Box,
  Flex,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
} from '~/design-system';
import BrandLogo from '../BrandLogo';
import Notification from '../Notification';
const VITE_CLASSIC_REGISTRATION_HOSTNAME = import.meta.env.VITE_CLASSIC_REGISTRATION_HOSTNAME;

/**
 * The props that this component needs.
 * @typedef {object} SendNotificationStepProps
 * @property {string} workspaceId
 * @property {string} importId
 */

const schema = yup.object().shape({
  segment: yup
    .string()
    .required('Please enter the segment.')
    .trim() // This is handy because when calling schema.cast() you can auto-trim the string.
    // It also checks the min length after trimming.
    .min(3, 'Please provide at least 3 characters.'),
  exhibitors: yup.array(),
});

/**
 * The view. The props are passed in from useMatchSupplier - all the logic resides there.
 * @param {{setFormikContext: function, onSubmit: (exhibitorIds: string[]) => void | Promise<void>} & ReturnType<useChoosePartners>} props
 * @returns
 */
export default function ChoosePartners({
  data,
  loading,
  notification,
  cancelNotification,
  setFormikContext,
  onSubmit,
}) {
  // This is important so that default value can be used for TextInput field
  if (loading) return null;
  const { collectionId, results } = data?.workspace?.dataImport;
  const registrationResults = results?.find(
    ({ object }) => object === 'REGISTRATION'
  );

  return (
    <React.Fragment>
      <Flex
        sx={{
          justifyContent: 'center',
          marginBottom: 6,
        }}
      >
        <Formik
          initialValues={{ segment: 'added' }}
          onSubmit={async (data) => {
            const exhibitors = getExhibitors({
              registrationResults,
              segment: data.segment,
            });

            await onSubmit(exhibitors?.map(({ exhibitorId }) => exhibitorId));
          }}
          validationSchema={schema}
        >
          {(formikContext) => {
            const exhibitors = getExhibitors({
              registrationResults,
              segment: formikContext.values.segment,
            });
            const hasSomeContacts = exhibitors.some(
              (exhibitor) => exhibitor.hasNotificationContacts
            );
            const someContactMissing = exhibitors.some(
              (exhibitor) => !exhibitor.hasNotificationContacts
            );

            setFormikContext({
              ...formikContext,
              isValid: formikContext.isValid && hasSomeContacts,
            });
            return (
              <Form>
                <SelectField
                  id="segment"
                  name="segment"
                  sx={{ width: '330px', marginBottom: 3 }}
                >
                  <option value="added">
                    Partners with new requests (
                    {registrationResults?.addedCount})
                  </option>
                  <option value="updated">
                    Partners with either new or updated requests (
                    {registrationResults?.addedCount +
                      registrationResults?.updatedCount}
                    )
                  </option>
                  <option value="all">
                    All partners (
                    {registrationResults?.addedCount +
                      registrationResults?.updatedCount +
                      registrationResults?.unchangedCount}
                    )
                  </option>
                  <option value="unchanged">
                    Partners without any changes (
                    {registrationResults?.unchangedCount})
                  </option>
                </SelectField>
                <Box sx={{ marginBottom: 3 }}>
                  {!hasSomeContacts && exhibitors?.length > 0 && (
                    <React.Fragment>
                      <Text sx={{ color: 'brand.warningRed' }}>
                        Add contacts to your partners in order to send
                        notifications.
                      </Text>
                    </React.Fragment>
                  )}
                  {hasSomeContacts && someContactMissing && (
                    <Text>
                      Partners without contacts will not receive this message.
                    </Text>
                  )}
                </Box>
                <ExhibitorsList
                  exhibitors={exhibitors}
                  collectionId={collectionId}
                />
              </Form>
            );
          }}
        </Formik>
      </Flex>

      {notification && (
        <Notification
          onClose={cancelNotification}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

function SelectField({ name, children, ...rest }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Field name={name} as="select" {...rest}>
        {children}
      </Field>
      <ErrorMessage name={name} component={Error} />
    </Box>
  );
}

function ExhibitorsList({ exhibitors, collectionId }) {
  return (
    <Table sx={{ width: '100%' }}>
      <TableBody>
        {exhibitors?.map((exhibitor) => (
          <TableRow>
            <BrandLogo brand={exhibitor} />
            <RowName exhibitor={exhibitor} collectionId={collectionId} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RowName({ exhibitor, collectionId, ...rest }) {
  const street = exhibitor?.street;
  const country = exhibitor?.country;
  const address = [street, country].filter(Boolean).join(', ');
  return (
    <TableCell
      sx={{
        height: '65px',
        textAlign: 'left',
        paddingLeft: 4,
      }}
      {...rest}
    >
      <Text variant="default">{exhibitor.name}</Text>
      <Text variant="smallest">{address}</Text>
      <Text
        variant="smallest"
        sx={{ color: 'brand.warningRed', height: '18px' }}
      >
        {!exhibitor.hasNotificationContacts && (
          <Trans>
            No contact information available.{' '}
            <Link
              to={`/dashboard/app/catalogs/${collectionId}/manage/registrations/${exhibitor.exhibitorId}/status/contacts`}
              target={`${collectionId}/${exhibitor.exhibitorId}`}
            >
              Add contact
            </Link>
          </Trans>
        )}
      </Text>
    </TableCell>
  );
}

function getExhibitors({ registrationResults, segment }) {
  if (!registrationResults) return [];
  const { added, updated, unchanged } = registrationResults;
  switch (segment) {
    case 'updated':
      return [...added.nodes, ...updated.nodes].sort((a, b) => {
        return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
      });
    case 'all':
      return [...added.nodes, ...updated.nodes, ...unchanged.nodes].sort(
        (a, b) => {
          return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
        }
      );
    case 'added':
      return [...added.nodes].sort((a, b) => {
        return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
      });
    case 'unchanged':
      return [...unchanged.nodes].sort((a, b) => {
        return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1;
      });
    default:
      return [];
  }
}

/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {SendNotificationStepProps} props
 * @returns
 */
export function useChoosePartners({ workspaceId, importId }) {
  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const queryResult = useQuery(query, {
    variables: {
      workspaceId,
      importId,
    },
  });
  const [_createCampaign] = useMutation(CREATE_CAMPAIGN);
  const [isCreatingCampaign, setIsCreatingCampaign] = React.useState(false);

  /**
   * @param {string[]} exhibitorIds
   */
  async function createCampaignAndRedirect(exhibitorIds) {
    const data = await createCampaign(exhibitorIds);
    if (!data?.result?.success) return;
    const campaignId = data?.result?.campaign?.campaignId;
    const collectionId = queryResult?.data?.workspace?.dataImport?.collectionId;
    if (campaignId && collectionId) {
      const path = `/dashboard/app/next/${encodeURIComponent(locale)}/${encodeURIComponent(workspaceId)}/c/${encodeURIComponent(collectionId)}/${encodeURIComponent(locale)}/campaigns/${encodeURIComponent(campaignId)}`;
      window.location.assign(`${VITE_CLASSIC_REGISTRATION_HOSTNAME}${path}`);
      return;
    }
  }
  /**
   * @param {string[]} exhibitorIds
   */
  async function createCampaign(exhibitorIds) {
    if (isCreatingCampaign) return;
    const collectionId = queryResult?.data?.workspace?.dataImport?.collectionId;
    const nodes =
      queryResult?.data?.workspace?.dataImport?.collection?.registrations?.nodes;
    const registrationIds = exhibitorIds
      .map((exhibitorId) =>
        nodes?.find((registration) => registration.companyId === exhibitorId)
          ?.registrationId
      )
      .filter(Boolean);

    setIsCreatingCampaign(true);
    try {
      const { data } = await _createCampaign({
        variables: {
          input: {
            collectionId,
            audience: {
              registrationIds,
              contactQuestions: ["Registration.users"]
            },
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message:
            data?.result?.message || 'There was an error, please try again!',
        });
      }
      setIsCreatingCampaign(false);
      return data;
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsCreatingCampaign(false);
      return { error: err };
    }
  }

  return {
    ...queryResult,
    createCampaign,
    createCampaignAndRedirect,
    isCreatingCampaign,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
  };
}

function Error({ children, ...rest }) {
  if (!children) return null;
  return (
    <Text sx={{ color: 'red', fontSize: 'small' }} {...rest}>
      {children}
    </Text>
  );
}

const query = gql`
  fragment ExhibitorData on Exhibitor {
    hasNotificationContacts
    exhibitorId
    name
    email
    street
    country
    logo {
      fixed(height: 80, width: 80, crop: PAD, fetch_format: AUTO) {
        src
        srcSet
      }
    }
  }

  query ChoosePartners($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        collectionId
        collection {
          collectionId
          ...on Event{
            eventId
          }
          registrations{
            nodes {
              registrationId
              companyId
            }
          }
        }
        results {
          object
          updatedCount
          updated {
            nodes {
              ... on Exhibitor {
                ...ExhibitorData
              }
            }
          }
          addedCount
          added {
            nodes {
              ... on Exhibitor {
                ...ExhibitorData
              }
            }
          }
          unchangedCount
          unchanged {
            nodes {
              ... on Exhibitor {
                ...ExhibitorData
              }
            }
          }
        }
      }
    }
  }
`;

/** @type {import('@apollo/client').DocumentNode} */
export const CREATE_CAMPAIGN = gql`
  mutation CreateNotificationCampaign($input: CreateCampaignInput!) {
    result: createCampaign(input: $input) {
      success
      code
      message
      campaign {
        campaignId
      }
    }
  }
`;
